import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config/api";
import Seo from "../Components/Seo";

const BG_COLORS = ["#1B5E20", "#0D47A1", "#B71C1C", "#4A148C", "#E65100", "#006064", "#000000", "#37474F"];

const timeAgo = (d) => {
  if (!d) return "";
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return "abhi";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

export default function Status() {
  const navigate = useNavigate();
  const mobileNo = localStorage.getItem("userMobileNumber") || "";
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [statusText, setStatusText] = useState("");
  const [bgColor, setBgColor] = useState(BG_COLORS[0]);
  const [creating, setCreating] = useState(false);
  const [msg, setMsg] = useState("");

  const [viewer, setViewer] = useState(null);
  const [viewIndex, setViewIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);
  const fileRef = useRef(null);

  const fetchStatuses = useCallback(async () => {
    if (!mobileNo) return;
    try {
      const res = await fetch(`${API_BASE_URL}/Chat/GetStatuses?mobileNo=${encodeURIComponent(mobileNo)}`);
      const data = await res.json();
      setGroups(Array.isArray(data) ? data : []);
    } catch {
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [mobileNo]);

  useEffect(() => {
    if (!mobileNo) {
      navigate("/login");
      return;
    }
    fetchStatuses();
  }, [mobileNo, navigate, fetchStatuses]);

  const markViewed = async (statusId) => {
    try {
      await fetch(`${API_BASE_URL}/Chat/ViewStatus`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ StatusId: statusId, ViewedBy: mobileNo }),
      });
    } catch {}
  };

  const createTextStatus = async () => {
    if (!statusText.trim()) return;
    setCreating(true);
    try {
      const res = await fetch(`${API_BASE_URL}/Chat/CreateStatus`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          MobileNo: mobileNo,
          MediaType: "text",
          Caption: statusText.trim(),
          TextColor: "#FFFFFF",
          BackgroundColor: bgColor,
        }),
      });
      if (!res.ok) throw new Error();
      setCreateOpen(false);
      setStatusText("");
      setMsg("✅ Status lag gayi!");
      fetchStatuses();
    } catch {
      setMsg("❌ Status nahi lag payi");
    } finally {
      setCreating(false);
      setTimeout(() => setMsg(""), 2500);
    }
  };

  const createImageStatus = async (file) => {
    if (!file) return;
    setCreating(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const up = await fetch(`${API_BASE_URL}/Chat/UploadStatusMedia`, { method: "POST", body: form });
      const upData = await up.json();
      const url = upData?.url || upData?.Url;
      if (!url) throw new Error();
      const res = await fetch(`${API_BASE_URL}/Chat/CreateStatus`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          MobileNo: mobileNo,
          MediaType: "image",
          MediaUrl: url,
          Caption: "",
        }),
      });
      if (!res.ok) throw new Error();
      setCreateOpen(false);
      setMsg("✅ Status lag gayi!");
      fetchStatuses();
    } catch {
      setMsg("❌ Image status nahi lag payi");
    } finally {
      setCreating(false);
      setTimeout(() => setMsg(""), 2500);
    }
  };

  // Viewer auto-advance
  useEffect(() => {
    if (!viewer) return;
    const current = viewer.Statuses[viewIndex];
    if (current) markViewed(current.Id);

    setProgress(0);
    const start = Date.now();
    const DURATION = 5000;
    timerRef.current = setInterval(() => {
      const pct = Math.min(((Date.now() - start) / DURATION) * 100, 100);
      setProgress(pct);
      if (pct >= 100) {
        if (viewIndex < viewer.Statuses.length - 1) setViewIndex((i) => i + 1);
        else {
          setViewer(null);
          setViewIndex(0);
          fetchStatuses();
        }
      }
    }, 80);
    return () => clearInterval(timerRef.current);
  }, [viewer, viewIndex, fetchStatuses]);

  const currentStatus = viewer?.Statuses?.[viewIndex];

  return (
    <div style={{ maxWidth: "620px", margin: "0 auto", padding: "24px" }}>
      <Seo title="Status — Share Moments" description="AIO India status — apne moments share karein, dosto ki status dekhein. 24 ghante ke baad auto delete." path="/status" />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "18px" }}>
        <h2 style={{ margin: 0, fontSize: "24px", fontWeight: 800, color: "#0f172a" }}>Status</h2>
        <button
          onClick={() => setCreateOpen(true)}
          style={{
            padding: "10px 18px", borderRadius: "999px", border: "none",
            background: "linear-gradient(135deg,#4f46e5,#7c3aed)", color: "#fff",
            fontWeight: 700, fontSize: "14px", cursor: "pointer",
            boxShadow: "0 4px 14px rgba(79,70,229,0.35)",
          }}
        >
          ➕ Status lagayein
        </button>
      </div>

      {msg && <div style={{ marginBottom: "12px", fontWeight: 700, fontSize: "14px", color: msg.includes("✅") ? "#16a34a" : "#dc2626" }}>{msg}</div>}

      {loading ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#94a3b8", fontWeight: 600 }}>Loading...</div>
      ) : groups.length === 0 ? (
        <div style={{ background: "#fff", borderRadius: "16px", padding: "40px", textAlign: "center", color: "#94a3b8", fontWeight: 600, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
          Abhi koi status nahi hai — pehli status aap lagayein! ✨
        </div>
      ) : (
        <div style={{ background: "#fff", borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
          {groups.map((g) => {
            const latest = g.Statuses?.[g.Statuses.length - 1];
            return (
              <div
                key={g.MobileNo}
                onClick={() => {
                  setViewer(g);
                  setViewIndex(0);
                }}
                style={{
                  display: "flex", alignItems: "center", gap: "14px",
                  padding: "13px 18px", borderBottom: "1px solid #f8fafc", cursor: "pointer",
                }}
              >
                <div
                  style={{
                    width: "52px", height: "52px", borderRadius: "50%", padding: "2.5px",
                    background: g.HasUnviewed
                      ? "linear-gradient(135deg,#4f46e5,#7c3aed,#ec4899)"
                      : "#cbd5e1",
                  }}
                >
                  <div
                    style={{
                      width: "100%", height: "100%", borderRadius: "50%", background: "#e2e8f0",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontWeight: 800, fontSize: "18px", color: "#475569",
                      border: "2.5px solid #fff",
                    }}
                  >
                    {(g.Name || "U")[0].toUpperCase()}
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "15px" }}>
                    {g.MobileNo === mobileNo ? "My Status" : g.Name || `****${String(g.MobileNo).slice(-4)}`}
                  </div>
                  <div style={{ fontSize: "12px", color: "#94a3b8" }}>
                    {g.Statuses?.length || 0} update(s) • {timeAgo(latest?.CreatedAt)}
                  </div>
                </div>
                {g.HasUnviewed && (
                  <span style={{ background: "#4f46e5", color: "#fff", fontSize: "10px", fontWeight: 800, padding: "3px 8px", borderRadius: "999px" }}>
                    NEW
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* CREATE MODAL */}
      {createOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.72)", zIndex: 1500, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}
          onClick={() => setCreateOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: "20px", padding: "22px", width: "100%", maxWidth: "400px" }}>
            <h3 style={{ margin: "0 0 14px", fontSize: "18px", fontWeight: 800, color: "#0f172a" }}>Status lagayein</h3>

            <div style={{ background: bgColor, borderRadius: "14px", minHeight: "120px", display: "flex", alignItems: "center", justifyContent: "center", padding: "16px", marginBottom: "12px" }}>
              <span style={{ color: "#fff", fontWeight: 700, fontSize: "17px", textAlign: "center" }}>{statusText || "Aapka status yahan dikhega..."}</span>
            </div>

            <textarea
              value={statusText}
              onChange={(e) => setStatusText(e.target.value.slice(0, 200))}
              placeholder="Kya soch rahe ho?"
              rows={2}
              style={{ width: "100%", padding: "11px 14px", borderRadius: "10px", border: "1px solid #e2e8f0", fontSize: "14px", resize: "none", boxSizing: "border-box", outline: "none" }}
            />

            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", margin: "12px 0" }}>
              {BG_COLORS.map((c) => (
                <button
                  key={c}
                  onClick={() => setBgColor(c)}
                  style={{
                    width: "30px", height: "30px", borderRadius: "50%", background: c, cursor: "pointer",
                    border: bgColor === c ? "3px solid #4f46e5" : "2px solid #e2e8f0",
                  }}
                />
              ))}
            </div>

            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={(e) => createImageStatus(e.target.files?.[0])}
            />

            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={createTextStatus}
                disabled={creating || !statusText.trim()}
                style={{ flex: 1, padding: "12px", borderRadius: "10px", border: "none", background: "linear-gradient(135deg,#4f46e5,#7c3aed)", color: "#fff", fontWeight: 700, cursor: "pointer", opacity: creating || !statusText.trim() ? 0.6 : 1 }}
              >
                {creating ? "..." : "Text Status"}
              </button>
              <button
                onClick={() => fileRef.current?.click()}
                disabled={creating}
                style={{ flex: 1, padding: "12px", borderRadius: "10px", border: "1px solid #e2e8f0", background: "#fff", color: "#334155", fontWeight: 700, cursor: "pointer" }}
              >
                📷 Photo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* VIEWER */}
      {viewer && currentStatus && (
        <div style={{ position: "fixed", inset: 0, background: "#000", zIndex: 2000, display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", gap: "4px", padding: "10px 12px 0" }}>
            {viewer.Statuses.map((_, i) => (
              <div key={i} style={{ flex: 1, height: "3px", borderRadius: "2px", background: "rgba(255,255,255,0.3)", overflow: "hidden" }}>
                <div style={{ height: "100%", width: i < viewIndex ? "100%" : i === viewIndex ? `${progress}%` : "0%", background: "#fff" }} />
              </div>
            ))}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "12px 16px", color: "#fff" }}>
            <div style={{ width: "36px", height: "36px", borderRadius: "50%", background: "#334155", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800 }}>
              {(viewer.Name || "U")[0].toUpperCase()}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: "14px" }}>{viewer.MobileNo === mobileNo ? "My Status" : viewer.Name || `****${String(viewer.MobileNo).slice(-4)}`}</div>
              <div style={{ fontSize: "11px", opacity: 0.7 }}>{timeAgo(currentStatus.CreatedAt)}</div>
            </div>
            <button onClick={() => { setViewer(null); fetchStatuses(); }} style={{ background: "transparent", border: "none", color: "#fff", fontSize: "22px", cursor: "pointer" }}>✕</button>
          </div>

          <div style={{ flex: 1, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div
              onClick={() => { if (viewIndex > 0) setViewIndex((i) => i - 1); }}
              style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "30%", zIndex: 2 }}
            />
            <div
              onClick={() => {
                if (viewIndex < viewer.Statuses.length - 1) setViewIndex((i) => i + 1);
                else { setViewer(null); setViewIndex(0); fetchStatuses(); }
              }}
              style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: "30%", zIndex: 2 }}
            />

            {currentStatus.MediaType === "image" && currentStatus.MediaUrl ? (
              <img src={currentStatus.MediaUrl} alt="status" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
            ) : (
              <div style={{ background: currentStatus.BackgroundColor || "#1B5E20", width: "86%", maxWidth: "420px", minHeight: "240px", borderRadius: "18px", display: "flex", alignItems: "center", justifyContent: "center", padding: "26px" }}>
                <span style={{ color: currentStatus.TextColor || "#fff", fontSize: "22px", fontWeight: 700, textAlign: "center", lineHeight: 1.5 }}>
                  {currentStatus.Caption}
                </span>
              </div>
            )}
          </div>

          <div style={{ padding: "14px", textAlign: "center", color: "rgba(255,255,255,0.6)", fontSize: "12px" }}>
            {viewIndex + 1} / {viewer.Statuses.length} • Aage badhne ke liye right side tap karein
          </div>
        </div>
      )}
    </div>
  );
}
